import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, Link } from 'react-router-dom'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { Mail, Lock, AlertCircle, Sun, Moon } from 'lucide-react'
import { useThemeContext } from '../contexts/ThemeContext'

const createLoginSchema = (t: (key: string) => string) => z.object({
  email: z.string().min(1, 'Email ou login ThorJava é obrigatório'),
  password: z.string().min(1, t('auth.password') + ' obrigatória'),
})

type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>

const LoginPage: React.FC = () => {
  const { t } = useTranslation()
  const loginSchema = React.useMemo(() => createLoginSchema(t), [t])
  const [error, setError] = useState<string | null>(null)
  const [sessionMessage, setSessionMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { login } = useAuth()
  const { mode, toggleMode } = useThemeContext()
  const navigate = useNavigate()

  useEffect(() => {
    const storedMessage = sessionStorage.getItem('session_expired_message')
    if (storedMessage) {
      setSessionMessage(storedMessage)
      sessionStorage.removeItem('session_expired_message')
      sessionStorage.removeItem('session_expired_timestamp')
    }
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    console.log('[LoginPage] 🔐 Iniciando login...')
    setError(null)
    setIsLoading(true)
    sessionStorage.removeItem('tokens_cleared')

    try {
      console.log('[LoginPage] [1/4] Chamando login()...')
      await login(data)
      console.log('[LoginPage] [2/4] Login concluído, redirecionando para a página inicial...')
      
      // No painel administrativo, o contexto de empresa não existe. Redirecionar diretamente para a raiz.
      navigate('/', { replace: true })
      
    } catch (err: any) {
      console.error('[LoginPage] ❌ Erro no login:', err)
      setError(err.response?.data?.detail || t('auth.invalidCredentials'))
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-6 sm:p-10">
      <div className="w-full max-w-2xl bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 p-10 sm:p-14 space-y-8 animate-fade-in-up">
        <div className="text-center space-y-3">
          <h1 className="text-5xl sm:text-6xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
            Galatea
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-300">
            Entre com suas credenciais para continuar
          </p>
        </div>

        {sessionMessage && (
          <div className="bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 p-5 rounded-lg flex items-start gap-3 border border-yellow-200 dark:border-yellow-700">
            <AlertCircle size={24} className="shrink-0 mt-0.5" />
            <span className="text-lg">{sessionMessage}</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-5 rounded-lg flex items-start gap-3 border border-red-200 dark:border-red-700">
            <AlertCircle size={24} className="shrink-0 mt-0.5" />
            <span className="text-lg">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Input
            label="Email ou Login"
            placeholder="seu@email.com"
            size="lg"
            {...register('email')}
            error={errors.email?.message}
            leftIcon={<Mail size={24} />}
          />

          <Input
            label="Senha"
            type="password"
            placeholder="••••••••"
            size="lg"
            {...register('password')}
            error={errors.password?.message}
            leftIcon={<Lock size={24} />}
          />

          <Button type="submit" className="w-full text-lg h-14" size="lg" isLoading={isLoading}>
            Entrar
          </Button>

          <div className="text-center">
            <Link
              to="/forgot-password"
              className="text-lg text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium hover:underline"
            >
              Esqueci minha senha
            </Link>
          </div>
        </form>

        <div className="pt-6 flex flex-col items-center gap-4 border-t border-slate-100 dark:border-slate-700">
          <button
            onClick={toggleMode}
            type="button"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-base font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
            {mode === 'dark' ? (
              <>
                <Sun size={20} /> Modo Claro
              </>
            ) : (
              <>
                <Moon size={20} /> Modo Escuro
              </>
            )}
          </button>
          <p className="text-base text-slate-400 dark:text-slate-500">
            &copy; {new Date().getFullYear()} Galatea Platform
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
